<template>
  <div ref="root" class="d-print-none p-0 archive" :style="archiveStyle">
    <component :is="productsViewStore.isMobileWidth ? Teleport : 'div'" class="d-contents" to=".content">
      <ProductsArchiveMenu
        v-if="!productsViewStore.fullscreen"
        :has-result="!!productsStore.currentProducts?.length"
        :view="productsViewStore.view"
        @switch-view="switchView($event.value)"
        @sync="showSyncModal = $event.value"
        @filters="showFilters = $event.value"
        @export="showExportModal = $event.value"
      />
    </component>

    <!-- Content -->
    <div>
      <div
        :class="{ 'sticky-wrapper': productsViewStore.view === 'table' }"
        :style="[{ 'z-index': 9 }, productsViewStore.view === 'table' ? productsViewStore.stickyWrapperStyle : '']"
        @mouseleave="showActiveFiltersInFullscreen = false"
      >
        <small v-if="productsViewStore.fullscreen" class="ml-1 link" @mouseenter="showActiveFiltersInFullscreen = true">Visa aktiva filter...</small>
        <div v-if="showActiveFiltersInFullscreen || !productsViewStore.fullscreen" :class="{ 'position-fixed bg-white w-100': productsViewStore.fullscreen }">
          <!-------------------->
          <!-- Active Filters -->
          <!-------------------->
          <ActiveFilters
            :key="store.translationLanguage"
            class="pt-1 px-3 pb-3"
            :query="route.query"
            is-active-filters
            max-height="19em"
            :data-culture="store.dataCulture"
            :included-countries="store.includedCountries"
            :include-products-missing-available-countries="store.includeProductsMissingAvailableCountries"
          />

          <div class="d-flex align-items-center">
            <button class="link ml-2 text-primary" @click="productsStore.fetchProducts(true)">
              <i class="fa fa-refresh" />
            </button>
            <em>
              <strong>{{ productsStore.currentTotalProducts }}</strong> {{ translate('products', 'application') }}
              <template v-if="getActiveFilters(route.query).length">
                {{ translate('match active filters', 'application') }}
              </template>
            </em>
            <SeasonalImageComponent class="ml-3" />
          </div>

          <!-- <hr class="m-0 mt-2" /> -->
        </div>
      </div>

      <!-------------->
      <!-- Products -->
      <!-------------->

      <!-- List view -->
      <div v-if="productsViewStore.view === 'list'" class="products">
        <template v-for="(product, rowIndex) of productsStore.currentProducts" :key="rowIndex">
          <ListProductRow
            ref="productRefs"
            :product="product"
            :data-index="rowIndex"
            class="product product-row"
            :class="{
              'last-active': product && store.lastActiveProduct?.sku === product.sku,
              'skeleton missing': !product,
            }"
          />
          <!-- :show-placeholder="!productIntersectionObserver.elementIsVisible(rowIndex)" -->
          <!-- placeholder causes issues in mobile (height not precise) -->

          <div v-if="rowIndex + 1 < productsStore.currentTotalProducts && (rowIndex + 1) % productsStore.fetchLimit === 0" class="page-divider">
            {{ getPageNr(rowIndex) }}
          </div>
        </template>

        <template v-if="!productsStore.allProductsLoaded">
          <ListProductRow
            v-for="n of skeletonProducts"
            :key="n"
            ref="skeletonRefs"
            :data-index="n + productsStore.currentProducts.length"
            class="product-row skeleton"
          />
        </template>
      </div>

      <!--  Table view -->
      <ProductsTableWrapper v-else-if="productsViewStore.view === 'table'" style="z-index: 0" class="products-table" />

      <!-- Grid view -->
      <div v-else-if="productsViewStore.view === 'grid'" class="products">
        <div class="products-grid">
          <GridProductCard
            v-for="(product, i) of productsStore.currentProducts"
            :key="i"
            ref="productRefs"
            :data-index="i"
            :product="product"
            class="product grid-card"
            :class="{ 'last-active': product && store.lastActiveProduct?.sku === product.sku, 'skeleton missing': !product }"
          />
          <!-- :show-placeholder="!productIntersectionObserver.elementIsVisible(i)" -->

          <template v-if="!productsStore.allProductsLoaded">
            <GridProductCard
              v-for="n of skeletonProducts"
              :key="n"
              ref="skeletonRefs"
              :data-index="n + productsStore.currentProducts.length"
              class="grid-card skeleton"
            />
          </template>
        </div>
      </div>

      <!---------------->
      <!-- Pagination -->
      <!---------------->
      <div class="sticky-wrapper z-1" :style="productsViewStore.view === 'table' ? productsViewStore.stickyWrapperStyle : ''">
        <small ref="bottom" class="d-block p-3 mb-2 text-center" style="margin-top: 3em">
          <template v-if="productsStore.allProductsLoaded"> {{ appTranslate('All products loaded') }} <i class="fa fa-thumbs-o-up" /> </template>
          <template v-else-if="productsStore.loading.has('fetch')"> {{ appTranslate('Loading') }}... </template>
        </small>

        <div v-if="showToTopButton" class="pagination-container">
          <div class="pagination">
            <button class="btn btn-secondary ml-auto" @click="scrollToTop()">
              {{ appTranslate('To the top') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--------------------------->
  <!-- Modal for Export view -->
  <!--------------------------->
  <ExportView
    v-show="showExportModal"
    v-model:export-product-sheet="printProductSheet"
    :activated="showExportModal"
    :total="productsStore.currentTotalProducts"
    @close="showExportModal = false"
  />

  <!------------------->
  <!-- Filters Modal -->
  <!------------------->
  <Modal v-show="showFilters" :activated="showFilters" type="off-canvas" position="left" @close="showFilters = false">
    <template #header>
      <h3>{{ appTranslate('Filter') }}</h3>
    </template>
    <FilterComponent :key="store.dataCulture?.language + store.translationLanguage" :activated="showFilters" />
    <template #footer>
      <strong>{{ productsStore.currentTotalProducts }}</strong>
      {{ translate('products', 'application') }}
      <template v-if="getActiveFilters(route.query).length">
        {{ translate('match active filters', 'application') }}
      </template>
      <div />
      <router-link v-if="filtersActive" class="text-nowrap" to="/">
        {{ appTranslate('Clear filters') }}
      </router-link>
    </template>
  </Modal>

  <!-------------------->
  <!-- Modal for Sync -->
  <!-------------------->
  <ECommerceSyncView v-show="showSyncModal" :activated="showSyncModal" :total="productsStore.currentTotalProducts" @close="showSyncModal = false" />

  <ProductSheetComponent
    v-if="printProductSheet"
    v-model:trigger-print="printProductSheet"
    :total="productsStore.currentTotalProducts"
    class="d-none d-print-block"
    @load="showExportModal = false"
  />
</template>

<script lang="ts" setup>
import Modal from '@/components/ModalComponent.vue'
import ProductSheetComponent from '@/components/export/ProductSheetComponent.vue'
import GridProductCard from '@/components/products/archive/GridProductCard.vue'
import ListProductRow from '@/components/products/archive/ListProductRow.vue'
import ProductsArchiveMenu from '@/components/products/archive/ProductsArchiveMenu.vue'
import ProductsTableWrapper from '@/components/products/archive/table/ProductsTableWrapper.vue'
import ActiveFilters from '@/components/products/filter/ActiveFiltersComponent.vue'
import FilterComponent from '@/components/products/filter/FilterComponent.vue'
import SeasonalImageComponent from '@/components/simple/SeasonalImageComponent.vue'
import { useIntersectionObserver } from '@/composables/useIntersectionObserver'
import { useTranslation } from '@/composables/useTranslation'
import { ProductView } from '@/models/ProductsArchive'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsViewStore } from '@/store/productsViewStore'
import { browserStorage } from '@/utils/browserStorage'
import { getActiveFilters } from '@/utils/tools'
import ECommerceSyncView from '@/views/ECommerceSyncView.vue'
import ExportView from '@/views/ExportView.vue'
import { ComponentPublicInstance, computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, StyleValue, Teleport, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// -----------
// -- Setup --
// -----------
defineOptions({ name: 'ProductsArchive' })

let preFetchScrollY = window.scrollY

// --------------------------
// -- Composables & Stores --
// --------------------------
const store = useDefaultStore()
const productsStore = useProductsStore()
const productsViewStore = useProductsViewStore()

const { translate, appTranslate } = useTranslation()
const route = useRoute()
const router = useRouter()

const productIntersectionObserver = useIntersectionObserver()
const skeletonIntersectionObserver = useIntersectionObserver()

// ---------------
// -- Reactives --
// ---------------
const root = ref<HTMLElement>()
const showExportModal = ref(false)
const showFilters = ref(false)
const showToTopButton = ref(false)
const showSyncModal = ref(false)
const printProductSheet = ref(false)
const showActiveFiltersInFullscreen = ref(false)

const productRefs = ref<ComponentPublicInstance[]>()
const skeletonRefs = ref<ComponentPublicInstance[]>()

// --------------------
// -- Computed props --
// --------------------
const filtersActive = computed(() => {
  const filterQueries = Object.entries(route.query).filter(([key]) => key?.startsWith('_'))
  return !!filterQueries?.length
})

const skeletonProducts = computed(() => Math.min(productsStore.fetchLimit, productsStore.currentTotalProducts - (productsStore.currentProducts?.length || 0)))

const archiveStyle = computed((): StyleValue => {
  if (!productsViewStore.fullscreen) {
    return productsViewStore.productSideMenuWidth
      ? { gridTemplateColumns: `${productsViewStore.productSideMenuWidth}px 1fr` }
      : { gridTemplateRows: 'auto 1fr' }
  }
  return undefined
})

// --------------
// -- Watchers --
// --------------
watch(
  [() => productsStore.currentProducts, () => productsViewStore.view],
  () => {
    if (productsViewStore.view === 'list' || productsViewStore.view === 'grid') {
      initSkeletonIntersectionObserver()
      observeIntersections()
    }
  },
  { deep: true },
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(async () => {
  activateView()
})
onActivated(() => activateView())
onDeactivated(() => deactivateView())
onUnmounted(() => deactivateView())

const activateView = () => {
  if (productsViewStore.view === 'list' || productsViewStore.view === 'grid') {
    initAllObservers()
  }

  document.removeEventListener('scroll', scrollListener)
  document.addEventListener('scroll', scrollListener)
}
const deactivateView = () => {
  document.removeEventListener('scroll', scrollListener)
  skeletonIntersectionObserver.disconnectIntersectionObserver()
  productIntersectionObserver.disconnectIntersectionObserver()
}

// --------------------
// -- Event Handlers --
// --------------------
const scrollListener = () => {
  showToTopButton.value = window.scrollY > 500
}

// ---------------
// -- Functions --
// ---------------

const initSkeletonIntersectionObserver = () => {
  skeletonIntersectionObserver.initIntersectionObserver(false, undefined, async (index) => {
    preFetchScrollY = window.scrollY
    const lastProductRow = productsStore.currentProducts.length - 1

    const fetched = await productsStore.loadProducts(index)
    if (fetched && !productIntersectionObserver.elementIsVisible(lastProductRow) && window.scrollY > preFetchScrollY) {
      window.scrollTo({ top: preFetchScrollY })
    }
  })
}

const observeIntersections = async () => {
  // table view handles intersection observer on its own
  await nextTick()
  const skeletonElements = root.value?.querySelectorAll('.product-row.skeleton[data-index], .grid-card.skeleton[data-index]')
  skeletonIntersectionObserver.observeAll(skeletonElements)

  const productElements = root.value?.querySelectorAll('.product-row.product[data-index], .grid-card.product[data-index]')
  productIntersectionObserver.observeAll(productElements)
}

const initAllObservers = () => {
  productIntersectionObserver.initIntersectionObserver()
  initSkeletonIntersectionObserver()
  observeIntersections()
}

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

const getPageNr = (index: number) => {
  const page = Math.ceil(index / productsStore.fetchLimit) + 1
  const totalPages = Math.ceil(productsStore.currentTotalProducts / productsStore.fetchLimit)
  return translate('Page %s of %s', 'application', 'capitalize', undefined, [page, totalPages])
}

const switchView = async (newView: ProductView) => {
  const navFail = await router.replace({ name: 'products', query: route.query, force: true }) // force navigation to trigger route guard
  if (!navFail) {
    productsViewStore.view = newView
    browserStorage.set('productsArchive-view', newView)

    if (newView === 'table') {
      productIntersectionObserver.disconnectIntersectionObserver()
      skeletonIntersectionObserver.disconnectIntersectionObserver()
    } else {
      initAllObservers()
    }
  }
}
</script>

<style lang="scss" scoped>
.archive {
  display: grid;
  min-width: fit-content;
  transition: grid-template-columns 0.3s ease;
}

.header {
  display: flex;

  @media (max-width: 575px) {
    display: block;
  }
}

.category-menu {
  width: 250px;

  @media (max-width: 767px) {
    width: 100%;
  }
}

.filter-toggle {
  width: 100%;
  max-width: 11em;
  overflow: hidden;

  i {
    margin-right: 0.5rem;
  }

  @media (max-width: 576px) {
    width: unset;
    i {
      margin-right: 0;
      padding: 0 0.5em;
    }

    span {
      display: none;
    }
  }
}

.modal-header {
  display: flex;
  justify-content: space-evenly;
}

.background {
  filter: blur(1px);
  height: 100%;
}

.pagination-container {
  position: relative;
  display: flex;
  justify-content: flex-end;

  .pagination {
    position: fixed;
    z-index: 20;
    bottom: 1em;

    margin: 1em 1em 0 0;
    white-space: nowrap;
  }
}

.btn-outline-warning:hover {
  color: white !important;
}

.products-grid {
  display: grid;
  margin: 1em;
  margin-right: max(4%, 1em);

  grid-gap: 1.5em;
  grid-row-gap: 1.5em;

  grid-template-columns: repeat(auto-fill, minmax(225px, 1fr));

  @media (max-width: 772px) {
    margin-left: 0.75em;
    margin-right: 0.75em;
    grid-gap: 0.75em;
    grid-row-gap: 0.75em;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));

    .product {
      max-width: 256px;
    }
  }
}

.products {
  margin-left: 0;

  .page-divider {
    display: flex;
    font-weight: bold;
    justify-content: center;
    background-color: #fafafa;
    padding: 0.5em;
    margin-top: 0.25em;
    margin-bottom: 0.25em;
  }

  .product {
    $inset-shadow: inset 0 0 20px 0 #f3eeea80;
    // $inset-shadow: inset 0 0 20px 0 #00000009;

    position: relative;
    border: 1px solid #eee;

    box-shadow: $inset-shadow;

    &.last-active .link {
      outline: none;
    }

    &.last-active,
    .preview-img {
      $colour: #00000025;
      border-color: $colour;
      box-shadow:
        $inset-shadow,
        0 0 20px 2px $colour;
    }

    .language {
      height: 1rem;
    }
  }
}

.tag-filter {
  width: 345px; // half of medium container breakpoint 690px
  padding-right: 0.5em;
  // z-index: 1;
}

.z-1 {
  z-index: 1;
}
</style>
